import React, { useEffect, useRef } from "react";
import Media from "@qlik/qlik/src/components/Media";
import RichText from "@qlik/qlik/src/components/RichText";
import ParallaxMove from "@qlik/qlik/src/lib/util/parallax";
import { getSpacingClassName } from "@qlik/qlik/src/lib/util/spacing";
import { getColorClassName } from "@qlik/qlik/src/lib/util/colors";
import { renderSanitizedHtmlContent } from "@qlik/qlik/src/lib/util/helpers";
import * as styles from "./styles.module.css";

/**
 * MediaWithCards component.
 * @param {Object} props - The component props.
 * @returns {JSX.Element} The rendered component.
 */

const MediaWithCards = ({ data }) => {
    // Destructure the data object
    const { title, cardItems = [], additionalConfiguration, numberOfColumns, customId } = data ?? {};
    // Get the padding class name based on the additional configuration
    const spacingClassNames = getSpacingClassName(additionalConfiguration, "");
    const { sectionColorMapping } = getColorClassName(data?.backgroundColor);
    // Check if the first card item has body content
    const hasBodyContent = cardItems?.[0]?.body;
    const [firstMediaItem, SecondMediaItem] = cardItems?.[0]?.media || [];

    const parallaxLayerOne = useRef(null);
    const parallaxLayerTwo = useRef(null);

    useEffect(() => {
        const parallaxAnimationTrigger = () => {
            ParallaxMove(parallaxLayerOne.current, {
                offset: 250,
                mobile: false,
                dir: "top",
                headerFixed: true,
                twoWay: false,
                parentEl: "section",
            });
            ParallaxMove(parallaxLayerTwo.current, {
                offset: 360,
                mobile: false,
                dir: "top",
                headerFixed: true,
                twoWay: false,
                parentEl: "section",
            });
        };

        parallaxAnimationTrigger();

        window.addEventListener("scroll", parallaxAnimationTrigger);
        return () => {
            window.removeEventListener("scroll", parallaxAnimationTrigger);
        };
    }, []);

    return (
        <section className={`${spacingClassNames} ${sectionColorMapping?.bgColor}`} id={customId}>
            <div className="container">
                {title && (
                    <h2
                        className={`text-center mb-7.5 md:mb-12.5 ${sectionColorMapping.primaryTextColor} ${styles.banner}`}
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML={{ __html: renderSanitizedHtmlContent(title) }}
                    />
                )}
                <div className={styles.cardsWrapper}>
                    <div className={`relative overflow-hidden parallex-wrp ${hasBodyContent ? "bodyContent" : ""} `}>
                        <div ref={parallaxLayerOne} className="relative md:top-0">
                            {firstMediaItem && (
                                <Media
                                    media={firstMediaItem}
                                    className="w-full object-cover rounded"
                                    videoClassName="rounded"
                                />
                            )}
                        </div>
                        <div ref={parallaxLayerTwo} className="hidden md:block absolute md:-bottom-2.5">
                            {SecondMediaItem && (
                                <Media
                                    media={SecondMediaItem}
                                    className="w-full object-cover rounded"
                                    videoClassName="rounded"
                                    lazyLoad={false}
                                />
                            )}
                        </div>
                    </div>

                    {cardItems?.slice(1) && (
                        <div className="relative z-1 mt-7.5 xl:mt-12.5">
                            <div
                                className={`grid gap-y-5 md:gap-x-7.5 
                                ${
                                    numberOfColumns === null
                                        ? "md:grid-cols-3"
                                        : `md:grid-cols-3 lg:grid-cols-${numberOfColumns || 3}`
                                }
                            `}
                            >
                                {cardItems?.slice(1).map((card) => (
                                    <div
                                        key={card?.id}
                                        className="shadow-lgLight p-3.75 md:p-5 rounded bg-white flex justify-between md:justify-start gap-x-5 md:flex-col"
                                    >
                                        {(card?.mediaIcon?.desktopMedia?.length ||
                                            card?.mediaIcon?.mobileMedia?.length) > 0 && (
                                            <div className="min-w-12">
                                                <Media
                                                    media={card.mediaIcon}
                                                    className="object-cover w-12 h-12 md:w-17 md:h-17 md:mb-7.5"
                                                />
                                            </div>
                                        )}
                                        <div className={styles.cardBody}>
                                            {card?.title && (
                                                <h3 className="text-base tracking-normal md:title-5">{card.title}</h3>
                                            )}
                                            {card?.body && (
                                                <div className="mt-4 md:mt-3.75">
                                                    <RichText data={card} />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
};

export default MediaWithCards;
